import React, { useEffect, useState, useRef } from 'react';
import { get } from '@/features/shared/lib/api';
import { parsePayload } from '@/features/shared/lib/utils';
import PublicationCard from '@/features/publication/components/PublicationCard';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';
import { useMetricsWidgets } from '@/features/shared/lib/metrics-widgets';

/**
 * ResearcherResources
 * - Full Resources tab content component.
 * - Props: active (boolean), className, record (researcher detail record)
 */
export default function ResearcherResources({ active, className = '', record }) {
  const containerRef = useRef(null);
  const base = `${active ? 'active' : 'hidden'} pt-[50px] lg:pt-[60px]`;

  // record에서 scholar_id 추출
  const scholarId = record?.scholar_id || record?.id || null;

  const [rows, setRows] = useState([]);
  const [pageInfo, setPageInfo] = useState({ totalResults: 0 });
  const [loading, setLoading] = useState(false);
  const didFetchRef = useRef(false);

  // API 호출
  useEffect(() => {
    if (!active || !scholarId || didFetchRef.current) return;

    let mounted = true;
    const fetchList = async () => {
      try {
        setLoading(true);
        const params = {
          current_page: 1,
          record_per_page: 5,
          scholar_id: scholarId,
        };

        const data = await get('/rm/v1/publications', { params });
        if (!mounted) return;

        const { status, records, pageInfo: pi } = parsePayload(data);
        if (!status) throw new Error('API status=false');

        setRows(records || []);
        setPageInfo({
          totalResults: pi?.totalResults ?? pi?.total_results ?? 0,
        });
        didFetchRef.current = true;
      } catch (error) {
        console.error('[ResearcherResources] Error fetching publications:', error);
        if (mounted) {
          setRows([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchList();
    return () => { mounted = false; };
  }, [active, scholarId]);

  // Altmetric/PlumX 위젯 스크립트 로드/초기화 (공통 훅)
  useMetricsWidgets({
    enabled: !loading && Array.isArray(rows) && rows.length > 0,
    delaysMs: { altmetric: 1000, plumx: 1000 },
    cleanupBeforeLoad: true,
    plumxRemoveRenderedDom: true,
    plumxRenderedSelector: '.plx-wrapping-print-link',
    plumxWireUpDelayMs: 150,
  });

  return (
    <div ref={containerRef} className={`researcher-resources-container ${base} ${className}`.trim()}>
      <h3 className="mb-4 text-[18px] font-bold leading-[100%] lg:mb-6">Author's Publications</h3>

      <div className="relative">
        {loading && (
          <ChartSkeletonLoader show={loading} />
        )}

        {!loading && rows.length > 0 && (
          <>
            <ul className="flex flex-col gap-4">
              {rows.map((item) => (
                <PublicationCard key={item.resource_id || item.id || Math.random()} item={item} showAltmetric={true} />
              ))}
            </ul>

            {pageInfo.totalResults > 5 && (
              <div className="text-center mt-10">
                <a
                  href={rmVars.homeUrl + `/publications/?scholar_id=${scholarId}`}
                  className="inline-flex items-center justify-center gap-1 py-3 px-8 bg-[#F3F5F9] border border-[#E4E8F1] rounded-xl"
                >
                  <span className="text-[14px] text-[#6C7687]">More</span>
                  <img
                    className="w-[16px]"
                    alt="link"
                    src={`${rmVars.themeUrl}images/launch-ico-6C7687.png`}
                  />
                </a>
              </div>
            )}
          </>
        )}

        {!loading && rows.length === 0 && (
          <div className="py-6 text-center text-[13px] font-semibold text-[#8D9AAF]">No publications</div>
        )}
      </div>
    </div>
  );
}
